// HalfDonut — the shared half-donut primitive (Phase 3).
//
// The reusable core for the half-donut type (First Goal, Set Piece; later Clean
// Sheet, BTTS). Same React+D3 split as RankedBars/StatPairBars: React owns the
// container + each donut's SVG *skeleton* (defs, bg, arc group, hole, centre
// text, light overlay) with NO JSX children inside the pieces D3 fills; D3 owns
// the arc data-join, gradients, clip, centre % text and the `.fg-stats`
// row inside a useEffect keyed on a signature of the donut data. Emits the same
// class names + DOM as the legacy `drawDonut` (verified by the visual A/B).
//
// Animation (the "no CSS transition" branch of the animate-decision rule): the
// arcs have no CSS transition, so D3 animates them. Whether a draw transitions
// is decided by whether it's the first draw for this mounted instance — so the
// CONSUMER controls resets via React `key`: First Goal keys <HalfDonut> by
// fixture (a fixture change → new teams → remount → arcs snap in, matching the
// legacy innerHTML rebuild), while Set Piece uses a stable key (its slots never
// change), so team/timeline changes animate current→new like the legacy redraw.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { arc as d3Arc, pie as d3Pie } from "d3-shape";
import { interpolate as d3Interpolate } from "d3-interpolate";
import { easeCubicInOut } from "d3-ease";
import type { DonutSegment } from "@bento/shared";

// arc/pie/interpolate are typed with minimal local signatures matching exactly
// how this file calls them (the real d3-shape/d3-interpolate generics are more
// general than needed here).

/** A datum an arc generator can render (start/end + optional radii). */
interface ArcDatum {
  startAngle: number;
  endAngle: number;
  innerRadius?: number;
  outerRadius?: number;
}
interface ArcGen {
  (d: ArcDatum): string;
  innerRadius(r: number): ArcGen;
  outerRadius(r: number): ArcGen;
  cornerRadius(r: number): ArcGen;
}
/** One arc produced by the pie layout (data + computed angles). */
interface PieArc {
  data: DonutSegment;
  value: number;
  index: number;
  startAngle: number;
  endAngle: number;
  padAngle: number;
}
interface PieGen {
  (data: DonutSegment[]): PieArc[];
  sort(compare: null): PieGen;
  value(fn: (d: DonutSegment) => number): PieGen;
  padAngle(a: number): PieGen;
  startAngle(a: number): PieGen;
  endAngle(a: number): PieGen;
}
const arcFactory = d3Arc as unknown as () => ArcGen;
const pieFactory = d3Pie as unknown as () => PieGen;
const interpolate = d3Interpolate as <T>(a: T, b: T) => (t: number) => T;

// Arc paths carry their last-drawn datum so a transition tweens from where the
// arc currently is (the animation rule), matching the legacy `this._current`.
type ArcPath = SVGPathElement & { _current?: PieArc };

const DURATION = 450;

/** Config for one donut in the strip. */
export interface DonutConfig {
  /** defs id namespace — unique within the module, no spaces (teamId / slot). */
  idNs: string;
  /** Value for the `data-{dataAttr}` attribute (full team name / slot label). */
  dataValue: string;
  /** The label above the donut. */
  label: string;
  segments: DonutSegment[];
  /** Centre % text; null hides it. */
  pct: { value: number; color: string } | null;
  /** `.fg-stats` row spans; null hides the row (only rendered when hasStats). */
  statsSpans: { cls: string; text: string }[] | null;
}

export interface HalfDonutProps {
  /** `data-team` (First Goal) vs `data-slot` (Set Piece) on the shell + svg. */
  dataAttr: "team" | "slot";
  /** defs id prefix, matching the legacy renderer ("fg" | "sp" | "bt" | "cs"). */
  idPrefix: string;
  /** Render the `.fg-stats` sibling div (First Goal only). */
  hasStats: boolean;
  /** Extra class(es) on the wrapper, e.g. "btts-donut-view" (default none). */
  wrapperClass?: string;
  donuts: DonutConfig[];
}

/**
 * Make a team-derived id namespace safe to use in an element id AND a CSS/d3
 * selector. `idNs` comes from a team name (MatchRate/FirstGoal pass
 * `team.replace(/\s+/g,"")`), so it can carry chars like `&` — and
 * `svg.select("#…&…")` throws "not a valid selector", an uncaught error that
 * blanks the whole canvas (e.g. "Brighton & Hove Albion"). Collapse everything
 * outside [A-Za-z0-9] to "-" (matches XgTimeline's teamKey). Distinct team names
 * stay distinct, and each select is scoped to its own <svg>, so no collisions.
 */
export function cssSafeId(idNs: string): string {
  return idNs.replace(/[^a-zA-Z0-9]/g, "-");
}

/** Port of the legacy `drawDonut` — draws one donut into its <svg>. */
function drawDonut(
  svgEl: SVGSVGElement,
  cfg: DonutConfig,
  idPrefix: string,
  hasStats: boolean,
  useTransition: boolean
): void {
  const W = svgEl.clientWidth;
  const H = svgEl.clientHeight;
  if (W < 10 || H < 10) return; // not laid out yet — the ResizeObserver retries

  const cx = W / 2;
  const outerR = Math.min(cx - 2, H - 36);
  const cy = Math.round((H + outerR - 28) / 2);
  const innerR = outerR * 0.71;
  const halfD = arcFactory()({
    startAngle: -Math.PI / 2,
    endAngle: Math.PI / 2,
    innerRadius: 0,
    outerRadius: outerR,
  });
  const holeD = arcFactory()({
    startAngle: -Math.PI / 2,
    endAngle: Math.PI / 2,
    innerRadius: 0,
    outerRadius: innerR,
  });
  // Sanitize the team-derived namespace at this single choke point (set + select
  // below stay consistent) — see cssSafeId for the Brighton "&" crash it prevents.
  const id = cssSafeId(cfg.idNs);
  const svg = select(svgEl);
  const dur = useTransition ? DURATION : 0;
  const pie = pieFactory()
    .sort(null)
    .value((d) => d.value)
    .padAngle(0.08)
    .startAngle(-Math.PI / 2)
    .endAngle(Math.PI / 2);
  const arcGen = arcFactory()
    .innerRadius(innerR)
    .outerRadius(outerR)
    .cornerRadius((outerR - innerR) * 0.35);
  const arcData = pie(cfg.segments);

  svg
    .select(".fg-bg")
    .attr("transform", `translate(${cx},${cy})`)
    .attr("d", halfD);

  // Light gradient — computed from the actual pixel position each render.
  let grad = svg.select<SVGRadialGradientElement>(`#${idPrefix}-light-${id}`);
  if (grad.empty()) {
    grad = svg
      .select("defs")
      .append("radialGradient")
      .attr("id", `${idPrefix}-light-${id}`);
    grad
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "white")
      .attr("stop-opacity", 0.18);
    grad
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "white")
      .attr("stop-opacity", 0);
  }
  grad
    .attr("gradientUnits", "userSpaceOnUse")
    .attr("cx", cx - outerR * 0.24)
    .attr("cy", cy - outerR * 0.56)
    .attr("r", outerR * 1.1);

  // Clip path — arcs only, so the light overlay won't touch the hole or gaps.
  let clipPath = svg.select<SVGClipPathElement>(`#${idPrefix}-clip-${id}`);
  if (clipPath.empty()) {
    clipPath = svg
      .select("defs")
      .append("clipPath")
      .attr("id", `${idPrefix}-clip-${id}`);
  }
  clipPath.attr("transform", `translate(${cx},${cy})`);
  const clipArcs = clipPath
    .selectAll<SVGPathElement, PieArc>("path")
    .data(arcData, (d) => d.data.key);
  clipArcs
    .enter()
    .append("path")
    .merge(clipArcs)
    .attr("d", (d) => arcGen(d));
  clipArcs.exit().remove();

  // Arc paths — keyed so enter/exit handles a segment-count change (2↔3).
  const g = svg
    .select(".fg-arcs")
    .attr("transform", `translate(${cx},${cy})`);
  const paths = g
    .selectAll<SVGPathElement, PieArc>("path")
    .data(
      arcData.map((d) => ({ ...d })),
      (d) => d.data.key
    );

  const entering = paths
    .enter()
    .append("path")
    .attr("class", "bento-fill")
    .attr("fill", (d) => d.data.color)
    .each(function (this: ArcPath, d) {
      this._current = { ...d, endAngle: d.startAngle };
    });

  entering
    .merge(paths)
    .attr("class", "bento-fill")
    .attr("fill", (d) => d.data.color)
    .transition()
    .duration(dur)
    .ease(easeCubicInOut)
    .attrTween("d", function (this: ArcPath, d) {
      const interp = interpolate(this._current as PieArc, d);
      return (t: number) => {
        this._current = interp(t);
        return arcGen(interp(t));
      };
    });

  paths
    .exit<PieArc>()
    .transition()
    .duration(dur)
    .ease(easeCubicInOut)
    .attrTween("d", function (this: ArcPath) {
      const cur = this._current as PieArc;
      return (t: number) =>
        arcGen(interpolate(cur, { ...cur, endAngle: cur.startAngle })(t));
    })
    .remove();

  // Hole + light overlay (drawn after the arcs so they sit on top).
  svg
    .select(".fg-hole")
    .attr("transform", `translate(${cx},${cy})`)
    .attr("d", holeD);
  svg
    .select(".fg-light-overlay")
    .attr("transform", `translate(${cx},${cy})`)
    .attr("d", halfD)
    .attr("fill", `url(#${idPrefix}-light-${id})`)
    .attr("clip-path", `url(#${idPrefix}-clip-${id})`);

  // Centre percentage text.
  const pctText = svg
    .select(".fg-pct-text")
    .attr("x", cx)
    .attr("y", cy + 14);
  if (cfg.pct) {
    pctText
      .style("display", null)
      .attr("fill", cfg.pct.color)
      .style("font-size", "var(--t-data)")
      .text(`${cfg.pct.value}%`);
  } else {
    pctText.style("display", "none");
  }

  // Stats row (First Goal Outcome mode). Its top margin is derived from the
  // donut geometry, so it's set here in the draw (matching the legacy renderer).
  if (hasStats) {
    const statsEl =
      svgEl.parentElement &&
      svgEl.parentElement.querySelector<HTMLElement>(".fg-stats");
    if (statsEl) {
      if (cfg.statsSpans) {
        statsEl.innerHTML = cfg.statsSpans
          .map((s) => `<span class="${s.cls}">${s.text}</span>`)
          .join("");
        statsEl.style.display = "";
        statsEl.style.marginTop = 8 - (H - cy) + "px";
      } else {
        statsEl.innerHTML = "";
        statsEl.style.display = "none";
        statsEl.style.marginTop = "";
      }
    }
  }
}

export default function HalfDonut({
  dataAttr,
  idPrefix,
  hasStats,
  wrapperClass,
  donuts,
}: HalfDonutProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRefs = useRef<(SVGSVGElement | null)[]>([]);
  // First draw for this mounted instance snaps (no transition); later draws (a
  // control change) animate. A consumer resets this via React `key` (remount).
  const firstRef = useRef(true);

  const sig = JSON.stringify(
    donuts.map((d) => [d.segments, d.pct, d.statsSpans])
  );

  useEffect(() => {
    const useTransition = !firstRef.current;
    firstRef.current = false;

    const drawAll = (withTransition: boolean) => {
      donuts.forEach((cfg, i) => {
        const svgEl = svgRefs.current[i];
        if (svgEl) drawDonut(svgEl, cfg, idPrefix, hasStats, withTransition);
      });
    };
    drawAll(useTransition);

    // Redraw (no transition) when the module is resized — the geometry is
    // pixel-derived. jsdom has no ResizeObserver; guard so tests don't throw.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => drawAll(false));
    const wrap = wrapRef.current;
    if (wrap) ro.observe(wrap);
    return () => ro.disconnect();
    // `sig` captures every draw input; `donuts`/`idPrefix`/`hasStats` read fresh.
  }, [sig, idPrefix, hasStats]);

  const cls = ["data-wrapper", "fg-wrapper", wrapperClass]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cls} ref={wrapRef}>
      {donuts.map((cfg, i) => (
        <div
          className="fg-team"
          {...{ [`data-${dataAttr}`]: cfg.dataValue }}
          key={cfg.dataValue}
        >
          <div className="fg-label">{cfg.label}</div>
          <svg
            className="fg-donut-svg"
            {...{ [`data-${dataAttr}`]: cfg.dataValue }}
            ref={(el) => {
              svgRefs.current[i] = el;
            }}
          >
            <defs />
            <path className="fg-bg" fill="var(--block-color)" />
            <g className="fg-arcs" />
            <path className="fg-hole" fill="var(--block-color)" />
            <text
              className="fg-pct-text"
              textAnchor="middle"
              dominantBaseline="middle"
              fontWeight="400"
              fontFamily="inherit"
            />
            <path className="fg-light-overlay" pointerEvents="none" />
          </svg>
          {hasStats && <div className="fg-stats" />}
        </div>
      ))}
    </div>
  );
}
